import React, { useState, useEffect, useMemo } from 'react';
import { ChartBarIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import Card from '../Card';
import Table from '../Table';
import Modal from '../Modal';
import Pagination from '../Pagination';
import SearchFilter from '../SearchFilter';

const ScenariosSection = ({ api, organizations = [], promptTemplates = [], onRefresh }) => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uniqueSpheres, setUniqueSpheres] = useState([]);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [orgFilter, setOrgFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 5;

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [templateVariables, setTemplateVariables] = useState([]);
  const [currentVarLabels, setCurrentVarLabels] = useState({});
  const [templateContent, setTemplateContent] = useState('');
  
  const defaultForm = { name: '', description: '', sphere: '', organization_id: null, prompt_template_id: null, variable_values: {}, variable_options: {} };
  const [form, setForm] = useState(defaultForm);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [scenariosData, spheresData] = await Promise.all([
        api.fetchScenarios(),
        api.fetchUniqueSpheres()
      ]);
      setScenarios(scenariosData);
      setUniqueSpheres(spheresData);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const orgIdToName = useMemo(() => {
    const map = {};
    organizations.forEach(o => { map[o.id] = o.name; });
    return map;
  }, [organizations]);

  const filtered = useMemo(() =>
    scenarios
      .filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()) || (s.description || '').toLowerCase().includes(searchQuery.toLowerCase()))
      .filter(s => {
        if (orgFilter === 'all') return true;
        if (orgFilter === 'global') return !s.organization_id;
        return String(s.organization_id) === orgFilter;
      }), [scenarios, searchQuery, orgFilter]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleOrgChange = async (orgId) => {
    setForm(p => ({ ...p, organization_id: orgId, prompt_template_id: null, variable_values: {} }));
    setTemplateVariables([]);
    setCurrentVarLabels({});
    setTemplateContent('');
    
    if (orgId) {
      try {
        const res = await api.authFetch(`/api/prompts/organization/${orgId}/template`);
        if (res.ok) {
          const data = await res.json();
          if (data.template) {
            setForm(p => ({ ...p, prompt_template_id: data.template.id }));
            setTemplateVariables(data.variables || []);
            setCurrentVarLabels(data.template.var_labels || {});
            setTemplateContent(data.template.content || '');
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleAdd = () => {
    setForm(defaultForm);
    setTemplateVariables([]);
    setCurrentVarLabels({});
    setTemplateContent('');
    setShowAddModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Добавляем sphere в variable_values
      const createData = {
        ...form,
        variable_values: {
          ...form.variable_values,
          sphere: form.sphere || ''
        }
      };
      await api.createScenario(createData);
      toast.success('Сценарий создан');
      setShowAddModal(false);
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = async (scenario) => {
    setCurrentScenario(scenario);
    setForm({
      name: scenario.name || '',
      description: scenario.description || '',
      sphere: scenario.sphere || '',
      organization_id: scenario.organization_id || null,
      prompt_template_id: scenario.prompt_template_id || null,
      variable_values: scenario.variable_values || {},
      variable_options: scenario.variable_options || {},
    });
    setTemplateContent('');
    if (scenario.prompt_template_id) {
      try {
        const vars = await api.fetchTemplateVariables(scenario.prompt_template_id);
        setTemplateVariables(vars);
        const template = promptTemplates.find(t => t.id === scenario.prompt_template_id);
        setCurrentVarLabels(template?.var_labels || {});
        setTemplateContent(template?.content || '');
      } catch {
        setTemplateVariables([]);
        setCurrentVarLabels({});
      }
    } else if (scenario.organization_id) {
      // Загрузить шаблон организации
      try {
        const res = await api.authFetch(`/api/prompts/organization/${scenario.organization_id}/template`);
        if (res.ok) {
          const data = await res.json();
          if (data.template) {
            setTemplateVariables(data.variables || []);
            setCurrentVarLabels(data.template.var_labels || {});
            setTemplateContent(data.template.content || '');
          }
        }
      } catch {
        setTemplateVariables([]);
        setCurrentVarLabels({});
      }
    } else {
      setTemplateVariables([]);
      setCurrentVarLabels({});
    }
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      // Добавляем sphere в variable_values
      const updateData = {
        ...form,
        variable_values: {
          ...form.variable_values,
          sphere: form.sphere || ''
        }
      };
      await api.updateScenario(currentScenario.id, updateData);
      toast.success('Сценарий обновлен');
      setShowEditModal(false);
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (scenarioId) => {
    if (!window.confirm('Удалить сценарий?')) return;
    try {
      await api.deleteScenario(scenarioId);
      toast.success('Сценарий удален');
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const getLabel = (varName) => currentVarLabels[varName] || varName;

  const columns = [
    { header: 'Название', render: (row) => (
      <div className="flex items-center gap-2">
        <span className="text-gray-900 dark:text-gray-100">{row.name}</span>
        <span className={`px-2 py-0.5 rounded-full text-[10px] ${row.organization_id ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'}`}>
          {row.organization_id ? (orgIdToName[row.organization_id] || 'Орг.') : 'Общий'}
        </span>
      </div>
    )},
    { header: 'Описание', render: (row) => <div className="max-w-xs text-gray-700 dark:text-gray-300 truncate" title={row.description}>{row.description || '—'}</div> },
    { header: 'Переменные', className: 'hidden lg:table-cell', render: (row) => {
      const vars = row.variable_values || {};
      const keys = Object.keys(vars).slice(0, 3);
      return <span className="text-gray-500 dark:text-gray-400 text-xs">{keys.length > 0 ? keys.join(', ') + (Object.keys(vars).length > 3 ? '...' : '') : '—'}</span>;
    }},
    { header: 'Действия', render: (row) => (
      <div className="flex gap-2">
        <button onClick={() => handleEdit(row)} className="bg-blue-600 text-white rounded-lg px-3 py-1 hover:bg-blue-700 flex items-center text-xs"><PencilIcon className="w-4 h-4 mr-1" />Ред.</button>
        <button onClick={() => handleDelete(row.id)} className="bg-red-600 text-white rounded-lg px-3 py-1 hover:bg-red-700 flex items-center text-xs"><TrashIcon className="w-4 h-4 mr-1" />Удал.</button>
      </div>
    )}
  ];

  // Рендер формы inline (не как компонент, чтобы не терять фокус)
  const renderForm = (onSubmit, submitText) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Название *</label>
        <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full h-12 px-4 rounded-xl border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" placeholder="Название сценария" required />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Описание *</label>
        <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full h-24 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 resize-none" placeholder="Описание сценария" required />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Сфера</label>
        <input 
          type="text" 
          list="spheres-list"
          value={form.sphere || ''} 
          onChange={e => setForm(p => ({ ...p, sphere: e.target.value }))} 
          className="w-full h-12 px-4 rounded-xl border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" 
          placeholder="Выберите или введите новую сферу" 
        />
        <datalist id="spheres-list">
          {uniqueSpheres.map((sphere, index) => (
            <option key={index} value={sphere} />
          ))}
        </datalist>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Шаблон промпта *</label>
        <select value={form.prompt_template_id || ''} onChange={async e => {
          const templateId = e.target.value ? +e.target.value : null;
          setForm(p => ({ ...p, prompt_template_id: templateId, variable_values: {} }));
          if (templateId) {
            try {
              const vars = await api.fetchTemplateVariables(templateId);
              setTemplateVariables(vars || []);
              const template = promptTemplates.find(t => t.id === templateId);
              setCurrentVarLabels(template?.var_labels || {});
              setTemplateContent(template?.content || '');
            } catch {}
          } else {
            setTemplateVariables([]);
            setCurrentVarLabels({});
            setTemplateContent('');
          }
        }} className="w-full h-12 px-4 rounded-xl border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" required>
          <option value="">Выберите шаблон</option>
          {promptTemplates.filter(t => t.id !== '__builtin__').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Организация (опционально)</label>
        <select value={form.organization_id || ''} onChange={e => setForm(p => ({ ...p, organization_id: e.target.value ? +e.target.value : null }))} className="w-full h-12 px-4 rounded-xl border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100">
          <option value="">Общий (для всех)</option>
          {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <p className="text-xs text-gray-500 mt-1">Если выбрана организация, сценарий будет доступен только её участникам</p>
      </div>

      {templateVariables.length > 0 && (
        <div className="p-4 bg-purple-50 dark:bg-purple-900/30 rounded-xl border border-purple-200 dark:border-purple-800">
          <h4 className="font-bold text-sm mb-3 text-purple-800 dark:text-purple-200">Переменные шаблона</h4>
          <div className="space-y-4">
            {templateVariables.map(v => (
              <div key={v.id} className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-purple-200 dark:border-purple-700">
                <label className="block text-sm font-medium text-purple-700 dark:text-purple-300 mb-2">
                  {getLabel(v.name)}
                  <code className="text-purple-500 text-xs ml-2">{`{{${v.name}}}`}</code>
                  {v.var_type === 'select' && <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">select</span>}
                </label>
                {v.var_type === 'select' ? (
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Варианты выбора (по одному на строку):</label>
                      <textarea
                        value={(form.variable_options || {})[v.name]?.join('\n') || (v.options || []).join('\n')}
                        onChange={e => {
                          const lines = e.target.value.split('\n');
                          setForm(p => ({ 
                            ...p, 
                            variable_options: { ...p.variable_options, [v.name]: lines }
                          }));
                        }}
                        onBlur={e => {
                          const lines = e.target.value.split('\n').filter(line => line.trim());
                          setForm(p => ({ 
                            ...p, 
                            variable_options: { ...p.variable_options, [v.name]: lines }
                          }));
                        }}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 h-24 resize-none text-sm"
                        placeholder="Вариант 1&#10;Вариант 2&#10;Вариант 3"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Выбранное значение:</label>
                      <select
                        value={(form.variable_values || {})[v.name] || ''}
                        onChange={e => setForm(p => ({ ...p, variable_values: { ...p.variable_values, [v.name]: e.target.value } }))}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">Выберите значение</option>
                        {((form.variable_options || {})[v.name] || v.options || []).filter(opt => opt.trim()).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </div>
                  </div>
                ) : v.var_type === 'textarea' ? (
                  <textarea
                    value={(form.variable_values || {})[v.name] || v.default_value || ''}
                    onChange={e => setForm(p => ({ ...p, variable_values: { ...p.variable_values, [v.name]: e.target.value } }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 h-20 resize-none"
                    placeholder={getLabel(v.name)}
                  />
                ) : (
                  <input
                    type={v.var_type === 'number' ? 'number' : 'text'}
                    value={(form.variable_values || {})[v.name] || v.default_value || ''}
                    onChange={e => setForm(p => ({ ...p, variable_values: { ...p.variable_values, [v.name]: e.target.value } }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    placeholder={getLabel(v.name)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {templateContent && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <h4 className="font-bold text-sm mb-2 text-gray-700 dark:text-gray-300">Шаблон промпта (только просмотр)</h4>
          <pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap font-mono bg-white dark:bg-gray-900 p-3 rounded-lg border max-h-48 overflow-y-auto">{templateContent}</pre>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={() => { setShowAddModal(false); setShowEditModal(false); }} className="flex-1 bg-gray-300 dark:bg-gray-600 text-black dark:text-gray-100 py-2 rounded-xl hover:bg-gray-400 dark:hover:bg-gray-500">Отмена</button>
        <button type="submit" className="flex-1 bg-green-600 text-white py-2 rounded-xl hover:bg-green-700">{submitText}</button>
      </div>
    </form>
  );

  return (
    <div data-section="scenarios">
      <Card title="Сценарии" icon={ChartBarIcon} headerActions={
        <button onClick={handleAdd} className="bg-green-500 text-white rounded-lg px-3 py-1 hover:bg-green-700 text-xs sm:text-base">Добавить</button>
      }>
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <SearchFilter value={searchQuery} onChange={setSearchQuery} placeholder="Поиск по названию или описанию" className="flex-1" />
          <select value={orgFilter} onChange={e => setOrgFilter(e.target.value)} className="border rounded-xl px-3 py-2 dark:bg-gray-900 dark:text-gray-100 text-sm">
            <option value="all">Все организации</option>
            <option value="global">Общие</option>
            {organizations.map(o => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
          </select>
        </div>
        <Table columns={columns} data={paginated} loading={loading} error={error} emptyMessage="Нет сценариев" />
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </Card>

      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Добавить сценарий" size="md">
        {renderForm(handleCreate, 'Добавить')}
      </Modal>

      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title={`Редактировать: ${currentScenario?.name}`} size="md">
        {renderForm(handleUpdate, 'Сохранить')}
      </Modal>
    </div>
  );
};

export default ScenariosSection;
